import {
  RightCircleOutlined,
  SearchOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Input,
  Space,
  Table,
  TableColumnType,
  Typography,
  DatePicker,
  Popover,
  Tag,
} from "antd";
import { debounce } from "lodash";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IQCFilter, IQCResult } from "../../models/Qc";
import { useGetQcResult } from "../../query/QcQuery";
import { renderQCStatusTag } from "../../utils/qcHelper";
import { notify } from "../../utils/toast";
import { qcService } from "../../services/qcService";
import dayjs, { Dayjs } from "dayjs";

// const REQUIRED_HEADERS = [
//   "Ngày hàng về/\r\nIncoming date",
//   "Ngày kiểm tra/\r\nInspection date",
//   "Dự án/\r\nProject",
//   "Mã dự án/\r\nProject code",
//   "Mã sản xuất/\r\nMPN",
//   "Nhà sản xuất/MFR",
//   "Mô tả ",
//   "Đơn vị/\r\nUnit",
//   "SL thực tế/\r\nActual Q'ty",
//   "AWB",
//   "Vị trí/\r\nLocation",
//   "Loại NCC/\r\nType",
//   "Người mua/\r\nPur",
//   "Week",
//   "Month",
//   "Loại vật liệu/\r\nMaterials",
//   "Nhà cung cấp/\r\nSupplier",
//   "Khách hàng/\r\nCustomer",
//   "Số lượng kiểm tra/\r\nSampling size",
//   "Số lượng lỗi/\r\nQ'ty NG",
//   "Tỷ lệ lỗi/\r\nNG rate",
//   "Kết luận/ Result",
//   "Date code",
//   "Xuất Xứ/\r\n",
//   "Độ nhạy ẩm/\r\nMSL",
//   "Tỷ lệ kiểm tra/\r\nInspection rate",
//   "Lỗi Thật/Ảo",
//   "Mô tả chi tiết/\r\nDetail description",
//   "Hành động/\r\nAction",
//   "Theo dõi/\r\nFollow",
//   "Loại lỗi/\r\nDefect",
//   "Người kiểm tra/\r\nInspector",
//   "Ghi chú ",
// ];

// const validateExcelFile = (file: File): Promise<string | null> => {
//   return new Promise((resolve) => {
//     const reader = new FileReader();
//     reader.onload = (e) => {
//       try {
//         const data = new Uint8Array(e.target?.result as ArrayBuffer);
//         const workbook = XLSX.read(data, { type: "array" });
//         const sheetName = workbook.SheetNames[0];
//         if (!sheetName) {
//           resolve("File không có sheet nào.");
//           return;
//         }
//         const sheet = workbook.Sheets[sheetName];
//         const rows = XLSX.utils.sheet_to_json<string[]>(sheet, {
//           header: 1,
//           defval: "",
//         });
//         const headerRow = (rows[0] as string[]) || [];
//         const missingHeaders = REQUIRED_HEADERS.filter(
//           (h) => !headerRow.some((cell) => String(cell) === h),
//         );
//         if (missingHeaders.length > 0) {
//           resolve(
//             `File không đúng định dạng. Thiếu ${missingHeaders.length} cột, ví dụ: "${missingHeaders[0].replace(/\r\n/g, " / ")}"`,
//           );
//           return;
//         }
//         if (rows.length < 2) {
//           resolve("File không có dữ liệu.");
//           return;
//         }
//         resolve(null);
//       } catch {
//         resolve("Không thể đọc file. Vui lòng kiểm tra lại định dạng.");
//       }
//     };
//     reader.readAsArrayBuffer(file);
//   });
// };

const WarehouseIssue: React.FC = () => {
  const [params, setParams] = useState<IQCFilter>({
    page: 1,
    limit: 10,
  });
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const navigate = useNavigate();
  const { data, isLoading } = useGetQcResult(params);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchChange = useCallback(
    debounce((field: string, value: string) => {
      setParams((prev) => ({
        ...prev,
        [field]: value || undefined,
        page: 1,
      }));
    }, 500),
    [],
  );

  const handleDownloadExcel = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      notify.error("Vui lòng chọn khoảng thời gian");
      return;
    }

    try {
      setIsDownloading(true);
      notify.info("Đang tải file...");
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");
      const response = await qcService.exportQcResult(startDate, endDate);
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `QC_Result_${startDate}_${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      notify.success("Tải file thành công");
    } catch (error) {
      notify.error("Lỗi khi tải file");
    } finally {
      setIsDownloading(false);
    }
  };

  const columns: TableColumnType<IQCResult>[] = [
    {
      title: "Ngày tạo",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v: string) => (v ? new Date(v).toLocaleString() : ""),
      align: "center",
    },
    {
      title: "Trạng thái",
      dataIndex: ["partNumber", "qcStatus"],
      render: (v: string) => renderQCStatusTag(v),
      align: "center",
    },
    {
      title: "Dự án",
      dataIndex: ["partNumber", "project"],
      key: "project",
      align: "center",
    },
    {
      title: "Mã dự án",
      dataIndex: ["partNumber", "projectCode"],
      key: "projectCode",
      align: "center",
    },
    {
      title: "Mã hàng",
      dataIndex: ["partNumber", "partNumber"],
      key: "partNumber",
      align: "center",
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm theo Part Number"
              onChange={(e) => {
                handleSearchChange("partNumber", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: params.partNumber ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Nhà sản xuất",
      dataIndex: ["partNumber", "manufacturer"],
      key: "manufacturer",
      align: "center",
    },
    {
      title: "AWB",
      dataIndex: ["partNumber", "awb"],
      key: "awb",
      align: "center",
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm theo AWB"
              onChange={(e) => {
                handleSearchChange("awb", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined style={{ color: params.awb ? "#1890ff" : undefined }} />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: IQCResult) => (
        <Space>
          <Button
            type="text"
            icon={<RightCircleOutlined />}
            onClick={() => navigate(`${record.id}`)}
          >
            Xem chi tiết
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        padding: "16px",
      }}
    >
      <div
        style={{
          display: "flex flex-col",
          flexWrap: "wrap",
          alignItems: "center",
          textAlign: "center",
          justifyContent: "center",
        }}
      >
        <Typography.Title
          level={4}
          style={{
            margin: 0,
            fontFamily: "Chakra Petch",
            textTransform: "uppercase",
          }}
          className="dark:text-white"
        >
          Danh sách kết quả sau khi QC
        </Typography.Title>
        <div className="text-gray-600">
          (chỉ hiển thị những mã hàng sau khi đã được kiểm tra và QC)
        </div>
        <div
          style={{
            marginTop: 12,
          }}
        >
          <Popover
            placement="bottomLeft"
            content={
              <div style={{ padding: "8px" }}>
                <DatePicker.RangePicker
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                  format="YYYY-MM-DD"
                  style={{ marginBottom: 8 }}
                />
                <Button
                  type="primary"
                  block
                  onClick={handleDownloadExcel}
                  loading={isDownloading}
                  disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                  icon={<DownloadOutlined />}
                >
                  Tải file
                </Button>
              </div>
            }
            trigger="click"
            title="Tải kết quả QC"
          >
            <Button
              icon={<DownloadOutlined />}
              type={
                dateRange && dateRange[0] && dateRange[1]
                  ? "primary"
                  : "default"
              }
            >
              Tải kết quả kiểm tra (
              {dateRange && dateRange[0] && dateRange[1]
                ? `${dateRange[0].format("DD/MM/YYYY")} ~ ${dateRange[1].format("DD/MM/YYYY")}`
                : "Chọn ngày"}
              )
            </Button>
          </Popover>
        </div>
      </div>

      <div className="w-full" style={{ overflowX: "auto", maxWidth: "100%" }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.data || []}
          loading={isLoading}
          pagination={{
            current: data?.pagination.page,
            pageSize: data?.pagination.limit,
            total: data?.pagination.total,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              setParams((prev) => ({ ...prev, page, limit: pageSize }));
            },
          }}
          tableLayout="fixed"
          scroll={{ x: "100%", y: 600 }}
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            overflow: "hidden",
          }}
          locale={{ emptyText: "Không có dữ liệu" }}
        />
      </div>
    </div>
  );
};

export default WarehouseIssue;
